import { FC, useEffect, useState } from "react";
import { ProjectProductMeterial } from "../../../../redux/types/Material";
import { useFetchProjectDetails } from "../../../../apolloClient/hooks/general/fetchHook";
import { useParams } from "react-router-dom";
import { ContractorTable } from "./ContractorTable";
import { useMutation } from "@apollo/client";
import { UPDATE_ORDER_STATUS } from "../../../../apolloClient/operation/mutations/mutation";
import { ProjectProductSystemOrderStatusInput, ComponentType, OrderStatusEnum } from "../../../../redux/types/common";
import { toast } from "react-toastify";
import { logEvent, PROJECT_EVENTS } from "../../../../utils/amplitude";
import { Box, Checkbox } from "@mui/material";
import { Visibility } from "@mui/icons-material";
import ConfirmationDialog from "../../../../components/dialogue/ConfirmationDialog";

interface MaterialProps {
  materials?: ProjectProductMeterial[]; // Optional prop
}

export const ContractorDetails: FC<MaterialProps> = ({ materials }) => {
  // const [materialList, setMaterialList] = useState<ProjectProductMeterial[]>(
  //   materials || [] 
  // );

  // State for confirmation dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingOrderChange, setPendingOrderChange] = useState<{
    productSystemId: number;
    statusType: 'orderSent' | 'orderConfirmed';
    value: boolean;
    currentOrderSent: boolean;
    currentOrderConfirmed: boolean;
    productSystemName?: string;
  } | null>(null);

  // State for removal and installation date selection
  const [removalDate, setRemovalDate] = useState<string>("");
  const [installationDate, setInstallationDate] = useState<string>("");

  // State for document selection
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<string>("glas");

  // Helper function to format date for display
  const createDate = (date: string) => {
    const newDate = new Date(date.includes('T') ? date : `${date}T12:00:00`);
    return newDate.toDateString();
  };

  // Helper function to format document type
  const formatDocumentType = (type: string) => {
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Toggle document selection
  const toggleDocumentSelection = (documentId: number) => {
    setSelectedDocuments(prev =>
      prev.includes(documentId)
        ? prev.filter(id => id !== documentId)
        : [...prev, documentId]
    );
  };

  // Handle viewing document in new tab
  const handleViewDocument = (documentUrl: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering the checkbox selection
    if (documentUrl) {
      window.open(documentUrl, '_blank', 'noopener,noreferrer');
    }
  };

  let { id } = useParams();

  const {
    projectDetails: projectDetailsData,
    loading: projectDetailsLoading,
    // error: projectDetailsError,
    refetch,
  } = useFetchProjectDetails({
    projectId: Number(id),
  });

  const [updateProjectProductSystemOrderStatus] = useMutation<
    { UpdateProjectProductSystemOrderStatus: boolean },
    { input: ProjectProductSystemOrderStatusInput }
  >(UPDATE_ORDER_STATUS);

  // Handle order status changes with confirmation
  const handleOrderStatusChange = async (
    productSystemId: number,
    statusType: 'orderSent' | 'orderConfirmed',
    value: boolean,
    currentOrderSent: boolean,
    currentOrderConfirmed: boolean,
    productSystemName?: string
  ) => {
    // Only show confirmation when checking the box (value === true)
    if (value) {
      setPendingOrderChange({
        productSystemId,
        statusType,
        value,
        currentOrderSent,
        currentOrderConfirmed,
        productSystemName,
      });
      setConfirmDialogOpen(true);
      // DO NOT update anything yet - wait for confirmation
    } else {
      // If unchecking, proceed without confirmation
      await executeOrderStatusChange(
        productSystemId,
        statusType,
        value,
        currentOrderSent,
        currentOrderConfirmed
      );
    }
  };

  // Handle changing order sent details (when order is already sent)
  const handleChangeOrderSent = (
    productSystemId: number,
    productSystemName?: string,
    existingRemovalDate?: string,
    existingInstallationDate?: string
  ) => {
    // Pre-populate the form with existing values
    if (existingRemovalDate) {
      // Convert from MM/DD/YYYY to YYYY-MM-DD for date input
      const parts = existingRemovalDate.split('/');
      if (parts.length === 3) {
        const [mm, dd, yyyy] = parts;
        setRemovalDate(`${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`);
      }
    }
    if (existingInstallationDate) {
      // Convert from MM/DD/YYYY to YYYY-MM-DD for date input
      const parts = existingInstallationDate.split('/');
      if (parts.length === 3) {
        const [mm, dd, yyyy] = parts;
        setInstallationDate(`${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`);
      }
    }
    
    // Open dialog to change the removal and installation dates
    setPendingOrderChange({
      productSystemId,
      statusType: 'orderSent',
      value: true, // Keep it as sent
      currentOrderSent: true,
      currentOrderConfirmed: false,
      productSystemName,
    });
    setConfirmDialogOpen(true);
  };

  // Execute the actual order status change
  const executeOrderStatusChange = async (
    productSystemId: number,
    statusType: 'orderSent' | 'orderConfirmed',
    value: boolean,
    currentOrderSent: boolean,
    currentOrderConfirmed: boolean
  ) => {
    try {
      // console.log("Order status change:", { productSystemId, statusType, value, currentOrderSent, currentOrderConfirmed });

      if (!productSystemId || productSystemId === 0) {
        toast("Invalid product system ID", { type: "error" });
        return;
      }

      // Format dates if provided
      let formattedRemovalDate = undefined;
      if (removalDate) {
        const [yyyy, mm, dd] = removalDate.split('-');
        formattedRemovalDate = `${mm}/${dd}/${yyyy}`;
      }

      let formattedInstallationDate = undefined;
      if (installationDate) {
        const [yyyy, mm, dd] = installationDate.split('-');
        formattedInstallationDate = `${mm}/${dd}/${yyyy}`;
      }

      // Determine the OrderStatus based on statusType and value
      let orderStatus: OrderStatusEnum;
      if (statusType === 'orderSent') {
        orderStatus = value ? OrderStatusEnum.SENT : OrderStatusEnum.NOT_SENT;
      } else { // statusType === 'orderConfirmed'
        orderStatus = value ? OrderStatusEnum.CONFIRMED : OrderStatusEnum.UNCONFIRMED;
      }

      // Prepare the mutation input with ComponentType.CONTRACTOR
      const input: ProjectProductSystemOrderStatusInput = {
        projectId: Number(id),
        productSystemId: productSystemId,
        componentType: ComponentType.CONTRACTOR,
        orderStatus: orderStatus,
        removalDate: formattedRemovalDate,
        installationDate: formattedInstallationDate,
        attachmentFileIds: selectedDocuments.length > 0 ? selectedDocuments : undefined,
        source: selectedBrand,
      };

      // Execute the mutation
      const { data } = await updateProjectProductSystemOrderStatus({
        variables: { input }
      });

      if (data?.UpdateProjectProductSystemOrderStatus) {
        toast(`Order ${statusType === 'orderSent' ? 'sent' : 'confirmed'} status updated!`, { 
          type: "success" 
        });
        
        // Track order status change
        logEvent(
          statusType === 'orderSent' ? PROJECT_EVENTS.CONTRACTOR_ORDER_SENT : PROJECT_EVENTS.CONTRACTOR_ORDER_CONFIRMED,
          {
            project_id: Number(id),
            product_system_id: productSystemId,
            status: value,
            removal_date: formattedRemovalDate || undefined,
            installation_date: formattedInstallationDate || undefined,
            timestamp: new Date().toISOString(),
          }
        );
        
        // Refetch the project details to get updated data
        refetch();
      } else {
        toast("Failed to update order status", { type: "error" });
      }
    } catch (error) {
      console.error("Error updating order status:", error);
      toast("Error updating order status", { type: "error" });
    }
  };

  // Handle confirmation dialog confirm
  const handleConfirmOrderChange = async () => {
    if (pendingOrderChange) {
      // Console log selected documents
      if (selectedDocuments.length > 0) {
        console.log("Selected Documents for Email Attachment:", selectedDocuments);
        const selectedDocsInfo = projectDetailsData?.documents
          ?.filter(doc => selectedDocuments.includes(doc.id))
          .map(doc => ({
            id: doc.id,
            fileName: doc.fileName,
            type: doc.type,
            url: doc.url
          }));
        console.log("Document Details:", selectedDocsInfo);
      }

      await executeOrderStatusChange(
        pendingOrderChange.productSystemId,
        pendingOrderChange.statusType,
        pendingOrderChange.value,
        pendingOrderChange.currentOrderSent,
        pendingOrderChange.currentOrderConfirmed
      );
    }
    setConfirmDialogOpen(false);
    setPendingOrderChange(null);
    // Clear form values
    setRemovalDate("");
    setInstallationDate("");
    setSelectedDocuments([]);
  };

  // Handle confirmation dialog cancel
  const handleCancelOrderChange = () => {
    setConfirmDialogOpen(false);
    setPendingOrderChange(null);
    // Clear form values
    setRemovalDate("");
    setInstallationDate("");
    setSelectedDocuments([]);
    // No refetch needed - checkbox state is controlled by server data and won't change
  };

  // Get dialog title and message based on status type
  const getDialogContent = () => {
    if (!pendingOrderChange) return { title: "", message: "" };
    
    const { statusType, productSystemName } = pendingOrderChange;
    const systemName = productSystemName || "this product system";
    
    if (statusType === 'orderSent') {
      return {
        title: "Confirm Order Sent",
        message: `Are you sure you want to mark the order as sent for ${systemName}?`,
      };
    } else {
      return {
        title: "Confirm Order Received",
        message: `Are you sure you want to confirm that the order has been received for ${systemName}?`,
      };
    }
  };

  // Trigger the refetch when the id changes (if needed)
  useEffect(() => {
    if (id) {
      refetch(); // Call refetch to make sure the query is updated when the id changes
    }
  }, [id, refetch]);

  const labourMeasurements =
    projectDetailsData?.insuranceScope?.laborMeasurements;

  const dialogContent = getDialogContent();

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ width: "100%", marginBottom: 2 }}>
        {projectDetailsLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 200,
              color: "#6b7280",
            }}
          >
            Loading...
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {labourMeasurements && labourMeasurements.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  padding: 6,
                  color: "#9ca3af",
                  fontSize: 14,
                }}
              >
                No data to display.
              </Box>
            ) : (
              <ContractorTable 
                labourMeasurements={labourMeasurements} 
                onOrderStatusChange={handleOrderStatusChange}
                onChangeOrderSent={handleChangeOrderSent}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        title={dialogContent.title}
        message={
          <Box>
            {/* Only show date inputs for Order Sent, not for Order Confirmed */}
            {pendingOrderChange?.statusType === 'orderSent' && (
              <>
                {/* Display Installation Date Range */}
                {projectDetailsData?.installationDetails?.fromDate && projectDetailsData?.installationDetails?.toDate && (
                  <Box
                    sx={{
                      marginBottom: 3,
                      padding: 2,
                      backgroundColor: '#f0f9ff',
                      borderRadius: 2,
                      border: '1px solid #bae6fd'
                    }}
                  >
                    <Box sx={{ fontWeight: 600, marginBottom: 1, fontSize: 14, color: '#0369a1' }}>
                      Installation Date Range (Customer Selected)
                    </Box>
                    <Box sx={{ fontSize: 14, color: '#0c4a6e' }}>
                      {createDate(projectDetailsData.installationDetails.fromDate)} - {createDate(projectDetailsData.installationDetails.toDate)}
                    </Box>
                  </Box>
                )}

                {/* Removal Date Input */}
                <Box sx={{ marginBottom: 2 }}>
                  <Box sx={{ fontWeight: 500, marginBottom: 1, fontSize: 14 }}>Removal Date</Box>
                  <input
                    type="date"
                    value={removalDate}
                    onChange={e => setRemovalDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 6,
                      border: '1px solid #e5e7eb',
                      fontSize: 14,
                    }}
                  />
                </Box>
                {/* Installation Date Input */}
                <Box sx={{ marginBottom: 2 }}>
                  <Box sx={{ fontWeight: 500, marginBottom: 1, fontSize: 14 }}>Installation Date</Box>
                  <input
                    type="date"
                    value={installationDate}
                    onChange={e => setInstallationDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 6,
                      border: '1px solid #e5e7eb',
                      fontSize: 14,
                    }}
                  />
                </Box>

                {/* Document Selector Section */}
                <Box sx={{ marginTop: 3 }}>
                  <Box sx={{ fontWeight: 600, marginBottom: 2, fontSize: 14, color: '#1f2937' }}>
                    Select Documents to Attach (Optional)
                  </Box>

                  {projectDetailsData?.documents && projectDetailsData.documents.length > 0 ? (
                    <Box
                      sx={{
                        maxHeight: 250,
                        overflowY: 'auto',
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        padding: 1
                      }}
                    >
                      {projectDetailsData.documents
                        .filter(doc => !doc.archived) // Only show non-archived documents
                        .map((doc) => (
                          <Box
                            key={doc.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: 1.5,
                              borderRadius: 1,
                              border: '1px solid transparent',
                              '&:hover': {
                                backgroundColor: '#f9fafb',
                                borderColor: '#e5e7eb'
                              },
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onClick={() => toggleDocumentSelection(doc.id)}
                          >
                            <Checkbox
                              checked={selectedDocuments.includes(doc.id)}
                              sx={{
                                padding: 0.5,
                                marginRight: 1
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDocumentSelection(doc.id);
                              }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ fontSize: 13, fontWeight: 500, color: '#1f2937', marginBottom: 0.25 }}>
                                {doc.fileName || `Document ${doc.id}`}
                              </Box>
                              <Box sx={{ fontSize: 11, color: '#6b7280' }}>
                                {formatDocumentType(doc.type)}
                              </Box>
                            </Box>
                            <Box
                              onClick={(e) => handleViewDocument(doc.url, e)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: '#2563eb',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease',
                                marginLeft: 1
                              }}
                            >
                              <Visibility sx={{ fontSize: 16 }} />
                            </Box>
                          </Box>
                        ))}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        padding: 2,
                        backgroundColor: '#f9fafb',
                        borderRadius: 2,
                        textAlign: 'center',
                        fontSize: 13,
                        color: '#6b7280',
                        border: '1px dashed #d1d5db'
                      }}
                    >
                      No documents available for this project
                    </Box>
                  )}
                </Box>
              </>
            )}
            {/* Brand Styling Selection */}
            <Box sx={{ marginBottom: 2, marginTop: 2 }}>
              <Box sx={{ fontWeight: 500, marginBottom: 1, fontSize: 14 }}>Brand Styling (for vendor email)</Box>
              <select
                value={selectedBrand}
                onChange={e => setSelectedBrand(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 6,
                  border: '1px solid #e5e7eb',
                  fontSize: 14,
                  backgroundColor: '#fff',
                }}
              >
                <option value="glas">Glas</option>
                <option value="waybe">Waybe</option>
              </select>
            </Box>
            <Box sx={{ fontSize: 14, color: "#374151", marginTop: 2 }}>{dialogContent.message}</Box>
          </Box>
        }
        onConfirm={handleConfirmOrderChange}
        onCancel={handleCancelOrderChange}
        confirmText="Confirm"
        cancelText="Cancel"
      />
    </Box>
  );
};
